import React from "react";
import {
    Box, Heading, Text, VStack, HStack, SimpleGrid, Badge, Icon, Card, Center, Link, Button
} from "@chakra-ui/react";
import { FiBookOpen, FiFile, FiExternalLink, FiPlay, FiDownload, FiInfo, FiFileText, FiImage, FiVideo, FiMusic, FiArchive } from "react-icons/fi";
import { motion } from "framer-motion";

const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'pdf': case 'doc': case 'docx': case 'xls': case 'xlsx': case 'ppt':
        case 'pptx': case 'txt': case 'rtf': return FiFileText;
        case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': case 'svg': return FiImage;
        case 'mp4': case 'avi': case 'mov': case 'wmv': case 'flv': return FiVideo;
        case 'mp3': case 'wav': case 'flac': case 'aac': return FiMusic;
        case 'zip': case 'rar': case '7z': case 'tar': case 'gz': return FiArchive;
        default: return FiFile;
    }
};

const getFileTypeColor = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'pdf': return 'red.500';
        case 'doc': case 'docx': return 'blue.500';
        case 'xls': case 'xlsx': return 'green.500';
        case 'ppt': case 'pptx': return 'orange.500';
        case 'txt': case 'rtf': return 'gray.500';
        case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': case 'svg': return 'purple.500';
        case 'mp4': case 'avi': case 'mov': case 'wmv': case 'flv': return 'pink.500';
        case 'mp3': case 'wav': case 'flac': case 'aac': return 'teal.500';
        case 'zip': case 'rar': case '7z': case 'tar': case 'gz': return 'yellow.500';
        default: return 'gray.500';
    }
};

const renderResourceItem = (item, resourceIndex, itemIndex, colorScheme, downloadFile) => {
    const colorStyles = {
        blue: { bg: "blue.50", border: "blue.200", icon: "blue.600", accent: "blue.500" },
        orange: { bg: "orange.50", border: "orange.200", icon: "orange.600", accent: "orange.500" }
    };
    const styles = colorStyles[colorScheme] || colorStyles.blue;

    if (item.type === "file") {
        const fileName = item.item_name.split("/").pop();
        const FileIcon = getFileIcon(fileName);
        const fileTypeColor = getFileTypeColor(fileName);
        return (
            <Card key={`file-${resourceIndex}-${itemIndex}`} p={4} mb={4} borderRadius="lg" borderWidth="1px" bg={styles.bg} borderColor={styles.border} _hover={{ shadow: "md", transform: "translateY(-2px)" }} transition="all 0.2s">
                <HStack spacing={3}>
                    <Box p={2} bg={fileTypeColor} borderRadius="md">
                        <Icon as={FileIcon} color="white" boxSize={4} />
                    </Box>
                    <VStack align="start" spacing={1} flex="1">
                        <HStack spacing={2}>
                            <Text fontWeight="600" fontSize="sm" color="gray.800">{item.name || item.item_name.split("/").pop()}</Text>
                            <Badge size="sm" colorScheme={fileTypeColor.split('.')[0]} fontSize="xs">{fileName.split('.').pop()?.toUpperCase()}</Badge>
                        </HStack>
                    </VStack>
                    <Link as="span" onClick={() => downloadFile(item.item_name)} cursor="pointer">
                        <Button size="sm" colorScheme={colorScheme} variant="outline" leftIcon={<FiDownload />}>Download</Button>
                    </Link>
                </HStack>
            </Card>
        );
    } else if (item.type === "link") {
        return (
            <Card key={`link-${resourceIndex}-${itemIndex}`} p={4} mb={4} borderRadius="lg" borderWidth="1px" bg={styles.bg} borderColor={styles.border} _hover={{ shadow: "md", transform: "translateY(-2px)" }} transition="all 0.2s">
                <HStack spacing={3}>
                    <Box p={2} bg={styles.accent} borderRadius="md">
                        <Icon as={FiExternalLink} color="white" boxSize={4} />
                    </Box>
                    <VStack align="start" spacing={1} flex="1">
                        <Text fontWeight="600" fontSize="sm" color="gray.800">{item.name || "External Resource"}</Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>{item.item_name}</Text>
                    </VStack>
                    <Link href={item.item_name} isExternal>
                        <Button size="sm" colorScheme={colorScheme} variant="outline" leftIcon={<FiExternalLink />}>Visit</Button>
                    </Link>
                </HStack>
            </Card>
        );
    } else if (item.type === "youtube") {
        const getYouTubeVideoId = (url) => {
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            return (match && match[2].length === 11) ? match[2] : null;
        };
        const videoId = getYouTubeVideoId(item.item_name);
        return (
            <Card key={`youtube-${resourceIndex}-${itemIndex}`} p={4} mb={4} borderRadius="lg" borderWidth="1px" bg={styles.bg} borderColor={styles.border} _hover={{ shadow: "md" }} transition="all 0.2s">
                <VStack align="stretch" spacing={4}>
                    <HStack spacing={3}>
                        <Box p={2} bg="red.500" borderRadius="md">
                            <Icon as={FiPlay} color="white" boxSize={4} />
                        </Box>
                        <VStack align="start" spacing={1} flex="1">
                            <Text fontWeight="600" fontSize="sm" color="gray.800">{item.name || "YouTube Video"}</Text>
                            <Text fontSize="xs" color="gray.500">Educational video content</Text>
                        </VStack>
                        <Badge colorScheme="red" fontSize="xs" px={2} py={1}>VIDEO</Badge>
                    </HStack>
                    {videoId ? (
                        <Box position="relative" width="100%" height="0" paddingBottom="56.25%" borderRadius="md" overflow="hidden" boxShadow="lg">
                            <iframe
                                src={`https://www.youtube.com/embed/${videoId}`}
                                title={item.name || "YouTube video player"}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", borderRadius: "6px" }}
                            />
                        </Box>
                    ) : (
                        <Box p={4} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.200">
                            <HStack>
                                <Icon as={FiInfo} color="red.500" />
                                <Text color="red.600" fontSize="sm">Invalid YouTube URL: {item.item_name}</Text>
                            </HStack>
                        </Box>
                    )}
                </VStack>
            </Card>
        );
    } else {
        return (
            <Card key={`text-${resourceIndex}-${itemIndex}`} p={4} mb={4} borderRadius="lg" borderWidth="1px" bg={styles.bg} borderColor={styles.border} _hover={{ shadow: "md" }} transition="all 0.2s">
                <HStack spacing={3} align="start">
                    <Box p={2} bg={styles.accent} borderRadius="md" mt={1}>
                        <Icon as={FiInfo} color="white" boxSize={4} />
                    </Box>
                    <VStack align="start" spacing={2} flex="1">
                        <Text fontWeight="600" fontSize="sm" color="gray.800">Course Information</Text>
                        <Text fontSize="sm" color="gray.700" whiteSpace="pre-line">{item.item_name}</Text>
                    </VStack>
                </HStack>
            </Card>
        );
    }
};

export const CourseResourcesTab = ({ courseResources, downloadFile }) => {
    const hasCourseResources = courseResources.length > 0;

    if (!hasCourseResources) {
        return (
            <Center py={10}>
                <Text color="gray.500">No course resources available.</Text>
            </Center>
        );
    }

    return (
        <Box>
            <Box mb={6} p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
                <HStack spacing={3} mb={3}>
                    <Icon as={FiBookOpen} color="blue.600" boxSize={5} />
                    <Heading size="md" color="blue.800">Course Resources Overview</Heading>
                </HStack>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Box textAlign="center">
                        <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                            {courseResources.reduce((total, r) => total + (r.items?.length || 0), 0)}
                        </Text>
                        <Text fontSize="sm" color="gray.600">Total Resources</Text>
                    </Box>
                    <Box textAlign="center">
                        <Text fontSize="2xl" fontWeight="bold" color="green.600">
                            {courseResources.reduce((total, r) => total + (r.items?.filter(i => i.type === 'file').length || 0), 0)}
                        </Text>
                        <Text fontSize="sm" color="gray.600">Documents</Text>
                    </Box>
                    <Box textAlign="center">
                        <Text fontSize="2xl" fontWeight="bold" color="red.600">
                            {courseResources.reduce((total, r) => total + (r.items?.filter(i => i.type === 'youtube').length || 0), 0)}
                        </Text>
                        <Text fontSize="sm" color="gray.600">Videos</Text>
                    </Box>
                    <Box textAlign="center">
                        <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                            {courseResources.reduce((total, r) => total + (r.items?.filter(i => i.type === 'link').length || 0), 0)}
                        </Text>
                        <Text fontSize="sm" color="gray.600">Links</Text>
                    </Box>
                </SimpleGrid>
            </Box>

            {courseResources.map((resource, resourceIndex) => {
                const hasItemsArray = resource.items && Array.isArray(resource.items);
                const isFlatStructure = !hasItemsArray && (resource.path || resource.name);
                return (
                    <motion.div key={`resource-${resourceIndex}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: resourceIndex * 0.1 }}>
                        <Box mb={6}>
                            {!isFlatStructure && (
                                <Box mb={4} p={4} bg="gray.50" borderRadius="lg" borderLeft="4px solid" borderLeftColor="blue.500">
                                    <HStack spacing={3}>
                                        <Box p={2} bg="blue.500" borderRadius="md">
                                            <Icon as={FiBookOpen} color="white" boxSize={4} />
                                        </Box>
                                        <VStack align="start" spacing={1}>
                                            <Heading size="md" color="gray.800">
                                                {resource.resource_name || resource.title || resource.name || 'Resource'}
                                            </Heading>
                                            <Text fontSize="sm" color="gray.600">{resource.items?.length || 0} items available</Text>
                                        </VStack>
                                    </HStack>
                                </Box>
                            )}
                            {hasItemsArray && resource.items.map((item, itemIndex) =>
                                renderResourceItem(item, resourceIndex, itemIndex, 'blue', downloadFile)
                            )}
                            {isFlatStructure && renderResourceItem({
                                type: 'file', item_name: resource.path || resource.name, name: resource.name
                            }, resourceIndex, 0, 'blue', downloadFile)}
                        </Box>
                    </motion.div>
                );
            })}
        </Box>
    );
};
